import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { EditMtrForm } from 'components/EditMtrForm';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { ModalDialog } from 'components/ModalDialog';
import { MtrTypeValue, SupplyByValue } from 'constantsProject';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { PriceListMtrRowData, UerUpdateMtrRowMutationVariables } from 'gql/graphql';
import { MtrRecordStatusEnum, WorkOrderStatusEnum, ChapterEnum, MtrTypeEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { useUpdateMtrToWorkOrderFromPriceList } from 'pages/Project/UerProject/WorkOrder/hooks/useUpdateMtrToWorkOrderFromPriceList';
import type { AnyObject } from 'types/AnyObject';
import { ExportCSVReport } from 'utils/createCSVReportFile';
import { replacerCost } from 'utils/replacerCost';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type React from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { useIsUniquePriceListMtrRowKey } from '../../WorkOrder/CreateMtrDialog';
import { useIsUniquePriceListMtrCode } from '../../WorkOrder/CreateMtrDialog/useIsUniquePriceListMtrCode';
import { updateMtrToActKC2FromPriceList } from '../../WorkOrder/workOrderQueries';

import { priceListMtrRow, updatePriceListMtrRow } from './editMtrQueries';

export const SupplyByItems = [
    { id: 'CONTRACTOR', label: 'Подрядчик' },
    { id: 'CUSTOMER', label: 'Заказчик' },
];

export const dataConvert = (data: PriceListMtrRowData): Record<string, any> => ({
    ...data,
    supplyBy: data.supplyBy && { id: data.supplyBy, label: SupplyByValue[data.supplyBy] },
    mtrType: data.mtrType && { id: data.mtrType, label: MtrTypeValue[data.mtrType] },
    unitCost: data.unitCost !== 0 ? data.unitCost : null,
});

interface IEditMtrDialogProps {
    updateMtr?: () => void;
    isOpenModal?: boolean;
    setIsOpenModal?: React.Dispatch<React.SetStateAction<boolean>>;
    resetPageData?: () => void;
    onCloseMatchDialog?: () => void;
    hideInformer?: boolean;
    chapter?: ChapterEnum | null;
    workOrderStatus?: WorkOrderStatusEnum;
    mtrId?: number;
    workOrderPriceId?: number;
}

const keyKey = 'key';
const statusKey = 'status';
const codeValueKey = 'code';
const mtrTypeValueKey = 'mtrType';
const supplyByValueKey = 'supplyBy';

export const EditMtrDialog = ({
    updateMtr,
    isOpenModal,
    setIsOpenModal,
    resetPageData,
    onCloseMatchDialog,
    hideInformer,
    chapter,
    workOrderStatus,
    mtrId: mtrIdProp,
    workOrderPriceId: workOrderPriceIdProp,
}: IEditMtrDialogProps) => {
    const { id: projectId, mtrId: mtrIdParam, workOrderPriceId: workOrderPriceIdParam, priceListId } = useParams();
    const { projectData } = useProjectData(projectId);
    const contractId = useMemo(() => projectData?.contractId, [projectData?.contractId]);

    const { mtrId, workOrderPriceId } = useMemo(
        () => ({
            mtrId: mtrIdProp ?? mtrIdParam,
            workOrderPriceId: workOrderPriceIdProp ?? workOrderPriceIdParam,
        }),
        [mtrIdParam, mtrIdProp, workOrderPriceIdParam, workOrderPriceIdProp],
    );

    const mtrTypeItems = useMemo(
        () => [
            ...(![ChapterEnum.Chapter8, ChapterEnum.Chapter9].includes(chapter as ChapterEnum)
                ? [
                      {
                          id: MtrTypeEnum.Equipment,
                          label: 'Оборудование',
                      },
                  ]
                : []),
            {
                id: MtrTypeEnum.Material,
                label: 'Материал',
            },
        ],
        [chapter],
    );

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isOpenWarningDialog, setIsOpenWarningDialog] = useState<boolean>(false);

    const { control, handleSubmit, reset, formState, watch, setError, clearErrors } = useForm<any>({
        defaultValues: {},
    });

    const keyValue = watch?.(keyKey);
    const codeValue = watch?.(codeValueKey);
    const mtrTypeValue = watch?.(mtrTypeValueKey);
    const supplyByValue = watch?.(supplyByValueKey);
    const statusValue = watch?.(statusKey);

    const isDirtyKeyValue = formState?.dirtyFields?.[keyKey];
    const isDirtyCodeValue = formState?.dirtyFields?.[codeValueKey];

    const { result: isKeyValueUnique } = useIsUniquePriceListMtrRowKey({
        contractId,
        mtrRowId: mtrId ? Number(mtrId) : null,
        isDirtyCodeValue: isDirtyKeyValue,
        key: keyValue as any,
    });

    const { result: isCodeValueUnique } = useIsUniquePriceListMtrCode({
        contractId,
        mtrRowId: mtrId ? Number(mtrId) : null,
        code: codeValue,
        isDirtyCodeValue,
        mtrType: mtrTypeValue?.id,
        supplyBy: supplyByValue?.id,
    });

    useEffect(() => {
        if (keyValue && !isKeyValueUnique) {
            setError?.(keyKey, { message: 'МТР с таким кодом уже существует' });
        } else {
            clearErrors?.(keyKey);
        }
    }, [clearErrors, isKeyValueUnique, keyValue, setError]);

    useEffect(() => {
        if (codeValue && !isCodeValueUnique) {
            setError?.(codeValueKey, {
                message: 'Шифр МТР уже существует в рамках выбранного типа и оператора поставки',
            });
        } else {
            clearErrors?.(codeValueKey);
        }
    }, [clearErrors, codeValue, isCodeValueUnique, isKeyValueUnique, keyValue, setError]);

    const [resetMtrRowData] = useResetStoreData({ requestId: 'MtrRow' });

    const mtrRowVariables = useMemo(() => ({ mtrRowId: Number(mtrId || priceListId) }), [mtrId, priceListId]);

    const [data] = useRequest({
        requestId: 'MtrRow',
        query: priceListMtrRow,
        variables: mtrRowVariables,
        withEffect: Boolean(mtrId || priceListId),
    });

    const {
        updateMtrToWorkOrderFromPriceListResponse,
        updateMtrToWorkOrderFromPriceListStatus,
        updateMtrToWorkOrderFromPriceListError,
        updateMtrToWorkOrderFromPriceListRequest,
        resetMtrUpdateData,
    } = useUpdateMtrToWorkOrderFromPriceList({
        workOrderPriceId: Number(workOrderPriceId),
        withEffect: false,
    });

    const [
        updatePriceListMtrRowData,
        updatePriceListMtrRowStatus,
        updatePriceListMtrRowError,
        updatePriceListMtrRowRequest,
    ] = useRequest({
        requestId: 'updateMtrRow',
        query: updatePriceListMtrRow,
        withEffect: false,
    });

    const [resetUpdateMtrRowData] = useResetStoreData({ requestId: 'updateMtrRow' });

    const preparedData = useMemo(
        () => data?.uerPriceListMtrRow?.data && dataConvert(data.uerPriceListMtrRow.data as PriceListMtrRowData),
        [data],
    );

    useEffect(() => {
        if (
            resetPageData &&
            updatePriceListMtrRowStatus === RequestStatus.SUCCESS &&
            updatePriceListMtrRowData?.uerUpdateMtrRow?.result
        ) {
            resetPageData();
        }
    }, [resetPageData, updatePriceListMtrRowData?.uerUpdateMtrRow?.result, updatePriceListMtrRowStatus]);

    useEffect(
        () => () => {
            resetMtrRowData(undefined);
            resetMtrUpdateData(undefined);
            resetUpdateMtrRowData(undefined);
            setIsOpenWarningDialog(false);
        },
        [resetMtrRowData, resetMtrUpdateData, resetUpdateMtrRowData],
    );

    useEffect(() => {
        if (preparedData) {
            reset(preparedData);
        }
    }, [preparedData, reset]);

    const formRef = useRef<HTMLFormElement>(null);

    const handleCloseEditMtr = useCallback(() => {
        if (formState.isDirty) {
            setIsOpenWarningDialog(true);
        } else if (setIsOpenModal) {
            resetMtrRowData(undefined);
            resetMtrUpdateData(undefined);
            setIsOpenModal(false);
        } else {
            navigate('../', { replace: true });
        }
    }, [formState.isDirty, navigate, resetMtrRowData, resetMtrUpdateData, setIsOpenModal]);

    const handleSubmitWarningDialog = useCallback(() => {
        if (setIsOpenModal) {
            setIsOpenModal(false);
        } else {
            resetMtrRowData(undefined);
            resetMtrUpdateData(undefined);
            navigate('../', { replace: true });
        }
    }, [navigate, resetMtrRowData, resetMtrUpdateData, setIsOpenModal]);

    const handleCancelWarningDialog = useCallback(() => {
        setIsOpenWarningDialog(false);
    }, []);

    const isSuccessUpdateMtrToWorkOrderFromPriceListRequest =
        updateMtrToWorkOrderFromPriceListStatus === RequestStatus.SUCCESS &&
        updateMtrToWorkOrderFromPriceListResponse?.uerUpdateMtrToWorkOrderFromPriceList?.result &&
        isSubmitted;

    const isSuccessUpdateMtrRowRequest =
        updatePriceListMtrRowStatus === RequestStatus.SUCCESS &&
        updatePriceListMtrRowData?.uerUpdateMtrRow?.result &&
        isSubmitted;

    const afterSuccessUpdateHandler = useCallback(() => {
        updateMtr?.();
        handleSubmitWarningDialog();

        if (onCloseMatchDialog) {
            onCloseMatchDialog();
        }

        if (!setIsOpenModal) {
            navigate('../', { replace: true, state: { reload: Math.random() } });
        }
    }, [handleSubmitWarningDialog, navigate, onCloseMatchDialog, setIsOpenModal, updateMtr]);

    useEffect(() => {
        if (isSuccessUpdateMtrToWorkOrderFromPriceListRequest || isSuccessUpdateMtrRowRequest) {
            afterSuccessUpdateHandler();
        }
    }, [afterSuccessUpdateHandler, isSuccessUpdateMtrRowRequest, isSuccessUpdateMtrToWorkOrderFromPriceListRequest]);

    const [, , , updateWorkOrderMtrToActKC2Request] = useRequest({
        requestId: 'updateMtrToActKC2FromPriceList',
        withEffect: false,
        query: updateMtrToActKC2FromPriceList,
        dependenciesUpdater: afterSuccessUpdateHandler,
    });

    const variablesInput = useCallback(
        (submitData: AnyObject) => ({
            mtrRowId: Number(mtrId || priceListId),
            ...(submitData.key && { key: submitData.key }),
            ...(submitData.code && { code: submitData.code }),
            ...(submitData.unitWeight && { unitWeight: replacerCost(submitData.unitWeight) }),
            ...(submitData.unitCost && { unitCost: replacerCost(submitData.unitCost) }),
            ...(submitData.supplyBy.id && { supplyBy: submitData.supplyBy.id }),
            ...(submitData.mtrType.id && { mtrType: submitData.mtrType.id }),
            ...(submitData.justification && { justification: submitData.justification }),
        }),
        [mtrId, priceListId],
    );

    const submitHandler = useCallback(
        (submitData: AnyObject) => {
            const input = variablesInput(submitData);

            if (workOrderStatus === WorkOrderStatusEnum.InProgress) {
                dispatch(
                    updateWorkOrderMtrToActKC2Request({
                        workOrderPriceId: Number(workOrderPriceId),
                        input,
                    }),
                );
            } else if (workOrderPriceId && (mtrId || priceListId)) {
                setIsSubmitted(true);

                dispatch(
                    updateMtrToWorkOrderFromPriceListRequest({
                        workOrderPriceId: Number(workOrderPriceId),
                        input,
                    }),
                );
            } else if (mtrId || priceListId) {
                const variables: UerUpdateMtrRowMutationVariables = {
                    input,
                    chapter: chapter ?? null,
                };

                setIsSubmitted(true);
                dispatch(updatePriceListMtrRowRequest(variables));
            }
        },
        [
            variablesInput,
            workOrderStatus,
            workOrderPriceId,
            mtrId,
            priceListId,
            dispatch,
            updateWorkOrderMtrToActKC2Request,
            updateMtrToWorkOrderFromPriceListRequest,
            updatePriceListMtrRowRequest,
            chapter,
        ],
    );

    const onCreateReport = useCallback(() => {
        ExportCSVReport(updateMtrToWorkOrderFromPriceListResponse?.uerUpdateMtrToWorkOrderFromPriceList?.validation);
    }, [updateMtrToWorkOrderFromPriceListResponse?.uerUpdateMtrToWorkOrderFromPriceList?.validation]);

    const onCloseModalError = useCallback(() => {
        setIsSubmitted(false);
    }, []);

    const isCodeFieldDisabled = useMemo(() => {
        const editableStatuses = [MtrRecordStatusEnum.NewRecord, MtrRecordStatusEnum.Draft];
        const isEditable = editableStatuses.includes(statusValue);

        return !isEditable;
    }, [statusValue]);

    return (
        <ModalDialog
            title="Редактирование МТР"
            onConfirm={handleSubmit(submitHandler)}
            onClose={handleCloseEditMtr}
            isOpen={isOpenModal || true}
            withTitleCloseButton
            btnConfirmLabel="Сохранить"
            btnConfirmDisabled={!formState.isDirty || !isKeyValueUnique || !isCodeValueUnique}
            isCommit={updateMtrToWorkOrderFromPriceListStatus === RequestStatus.FETCHING}
            isOnClickOutsideDisable
        >
            <EditMtrForm
                ref={formRef}
                control={control}
                mtrTypeItems={mtrTypeItems}
                supplyByItems={SupplyByItems}
                hideInformer={hideInformer}
                informerText={
                    'Новые позиции МТР будут добавлены в Прейскурант МТР. Шифр для новых позиций будет присвоен после заполнения параметров "Тип" и "Поставка"'
                }
                disabledFieldRules={{
                    code: isCodeFieldDisabled,
                }}
            />
            <ConfirmationDialog
                textHeader="ОТМЕНА РЕДАКТИРОВАНИЯ МТР"
                textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                confirmButtonText="Отменить"
                cancelButtonText="Не отменять"
                onSubmit={handleSubmitWarningDialog}
                isOpened={isOpenWarningDialog}
                onClose={handleCancelWarningDialog}
            />
            <ErrorHandlingModal
                titleText="Редактирование МТР"
                isSubmitted={isSubmitted}
                statusRequest={updateMtrToWorkOrderFromPriceListStatus || updatePriceListMtrRowStatus}
                dataRequest={
                    updateMtrToWorkOrderFromPriceListResponse?.uerUpdateMtrToWorkOrderFromPriceList ||
                    updatePriceListMtrRowData?.uerUpdateMtrRow
                }
                errorsRequest={updateMtrToWorkOrderFromPriceListError || updatePriceListMtrRowError}
                handleCloseModalError={onCloseModalError}
                handleCreateReport={onCreateReport}
            />
        </ModalDialog>
    );
};

EditMtrDialog.displayName = 'EditMtrDialog';
